import { useEffect, useRef, useState, useCallback } from "react";
import { useAgentStream, useAgentLogs, useOrchestrate, type StreamAgentQuestion } from "@/hooks/use-agents";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { AgentLog, Workspace } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BrainCircuit,
  Zap,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Play,
  Square,
  Terminal,
  Sparkles,
  History,
  Radio,
  MessageSquare,
  Send,
  ChevronDown,
  ChevronRight,
  Clock,
} from "lucide-react";

const AGENT_COLORS: Record<string, string> = {
  Draper: "text-blue-400",
  Peggy: "text-pink-400",
  Pete: "text-amber-400",
  Rogers: "text-green-400",
  System: "text-muted-foreground",
};

const AGENT_BORDER_COLORS: Record<string, string> = {
  Draper: "border-l-blue-500/50",
  Peggy: "border-l-pink-500/50",
  Pete: "border-l-amber-500/50",
  Rogers: "border-l-green-500/50",
  System: "border-l-white/20",
};

const AGENT_DOT_COLORS: Record<string, string> = {
  Draper: "bg-blue-500",
  Peggy: "bg-pink-500",
  Pete: "bg-amber-500",
  Rogers: "bg-green-500",
};

const AGENT_ROLES: Record<string, string> = {
  Draper: "Strategist",
  Peggy: "Content",
  Pete: "Ads",
  Rogers: "Leads",
};

function TypeIcon({ type }: { type: string }) {
  switch (type) {
    case "error":
      return <AlertCircle size={12} className="text-red-400" />;
    case "milestone":
      return <CheckCircle2 size={12} className="text-emerald-400" />;
    case "action":
      return <Zap size={12} className="text-amber-400" />;
    case "system":
      return <Terminal size={12} className="text-muted-foreground" />;
    case "reasoning":
    case "thought":
      return <BrainCircuit size={12} className="text-blue-400" />;
    default:
      return <BrainCircuit size={12} className="text-blue-400/60" />;
  }
}

function StepBadge({ iteration, maxIterations }: { iteration?: number; maxIterations?: number }) {
  if (!iteration) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[8px] font-mono bg-blue-500/15 text-blue-300/70 border border-blue-500/20 rounded px-1 py-0.5 shrink-0">
      <Clock size={7} />
      {maxIterations ? `${iteration}/${maxIterations}` : `Step ${iteration}`}
    </span>
  );
}

function LogEntry({ log, isLive, index }: { log: AgentLog; isLive: boolean; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const meta = (log.metadata || {}) as Record<string, any>;
  const isReasoning = log.type === "reasoning" || log.type === "thought";
  const hasDetails = !!(log.details && log.details !== log.action);

  return (
    <div
      className={`flex items-start gap-2 pl-2 border-l-2 py-1.5 px-2 rounded-r-md animate-in fade-in slide-in-from-bottom-1 duration-300 ${
        isReasoning
          ? `bg-blue-950/20 ${AGENT_BORDER_COLORS[log.agent] || "border-l-blue-500/30"}`
          : log.type === "milestone"
          ? `bg-emerald-950/10 ${AGENT_BORDER_COLORS[log.agent] || "border-l-emerald-500/30"}`
          : log.type === "error"
          ? "bg-red-950/10 border-l-red-500/40"
          : log.type === "warning"
          ? "bg-amber-950/10 border-l-amber-500/40"
          : `bg-muted/30 ${AGENT_BORDER_COLORS[log.agent] || "border-l-white/10"}`
      }`}
      data-testid={`stream-log-${log.id || index}`}
    >
      <TypeIcon type={log.type} />
      <span className={`font-bold shrink-0 ${AGENT_COLORS[log.agent] || "text-muted-foreground"}`}>
        {log.agent}
      </span>
      <div className="flex-1 min-w-0">
        <span className={`${isReasoning ? "text-foreground/85 italic" : "text-foreground/70"} text-xs`}>
          {log.action}
        </span>
        {hasDetails && expanded && (
          <div className="mt-1 text-[10px] text-muted-foreground/60 bg-black/20 rounded p-1.5 font-mono whitespace-pre-wrap break-words border border-white/5">
            {log.details}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <StepBadge iteration={meta.iteration ?? meta.step} maxIterations={meta.maxIterations ?? meta.total} />
        {hasDetails && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors"
            data-testid={`button-expand-log-${log.id || index}`}
          >
            {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          </button>
        )}
        {log.status === "running" && (
          <Loader2 size={10} className="animate-spin text-blue-400" />
        )}
        {!isLive && log.createdAt && (
          <span className="text-[9px] text-muted-foreground/40">{timeAgo(log.createdAt as unknown as string)}</span>
        )}
      </div>
    </div>
  );
}

function Starfield() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const stars: { x: number; y: number; z: number; pz: number }[] = [];
    const STAR_COUNT = 200;

    function resize() {
      if (!canvas) return;
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    }
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < STAR_COUNT; i++) {
      stars.push({
        x: Math.random() * canvas.width - canvas.width / 2,
        y: Math.random() * canvas.height - canvas.height / 2,
        z: Math.random() * canvas.width,
        pz: 0,
      });
    }

    function draw() {
      if (!canvas || !ctx) return;
      ctx.fillStyle = "rgba(0,0,0,0.15)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const speed = 1.5;

      for (const star of stars) {
        star.pz = star.z;
        star.z -= speed;
        if (star.z <= 0) {
          star.x = Math.random() * canvas.width - cx;
          star.y = Math.random() * canvas.height - cy;
          star.z = canvas.width;
          star.pz = star.z;
        }

        const sx = (star.x / star.z) * cx + cx;
        const sy = (star.y / star.z) * cy + cy;
        const r = Math.max(0, (1 - star.z / canvas.width) * 2);
        const brightness = Math.max(0, 1 - star.z / canvas.width);

        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(100, 140, 255, ${brightness * 0.7})`;
        ctx.fill();
      }
      animId = requestAnimationFrame(draw);
    }
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.6 }}
    />
  );
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  if (m > 0) return `${m}m ago`;
  return "just now";
}

interface AgentRun {
  id: string;
  agent: string;
  logs: AgentLog[];
  isSubAgent: boolean;
}

function groupLogsIntoRuns(logs: AgentLog[]): AgentRun[] {
  const runs: AgentRun[] = [];
  for (let i = 0; i < logs.length; i++) {
    const log = logs[i];
    const last = runs[runs.length - 1];
    if (last && last.agent === log.agent) {
      last.logs.push(log);
    } else {
      runs.push({
        id: String(log.id ?? i),
        agent: log.agent,
        logs: [log],
        isSubAgent: log.agent !== 'Draper' && log.agent !== 'System',
      });
    }
  }
  return runs;
}

const AGENT_FULL_BORDER_COLORS: Record<string, string> = {
  Draper: 'border-blue-500/30',
  Peggy: 'border-pink-500/30',
  Pete: 'border-amber-500/30',
  Rogers: 'border-green-500/30',
  System: 'border-white/10',
};

function AgentRunGroup({ run, isLive, startIndex }: { run: AgentRun; isLive: boolean; startIndex: number }) {
  const [open, setOpen] = useState(isLive);
  if (!run.isSubAgent) {
    return (
      <>
        {run.logs.map((log, i) => (
          <LogEntry key={log.id || startIndex + i} log={log} isLive={isLive} index={startIndex + i} />
        ))}
      </>
    );
  }
  const milestoneLog = run.logs.find(l => l.type === 'milestone');
  const summary = (milestoneLog?.action || run.logs[run.logs.length - 1]?.action || '').slice(0, 90);
  const stepCount = run.logs.length;
  const hasError = run.logs.some(l => l.type === 'error');
  const borderColor = hasError ? 'border-red-500/30' : (AGENT_FULL_BORDER_COLORS[run.agent] || 'border-white/10');
  const dotColor = AGENT_DOT_COLORS[run.agent] || 'bg-muted-foreground/40';
  return (
    <div className={`border rounded-lg overflow-hidden ${borderColor}`} data-testid={`agent-run-group-${run.id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors"
        data-testid={`button-toggle-agent-run-${run.id}`}
      >
        <div className={`w-4 h-4 rounded-full ${dotColor} flex items-center justify-center text-[8px] font-bold text-background shrink-0`}>
          {run.agent[0]}
        </div>
        <span className={`text-xs font-bold shrink-0 ${AGENT_COLORS[run.agent] || 'text-muted-foreground'}`}>{run.agent}</span>
        <span className="text-xs text-muted-foreground/60 flex-1 min-w-0 truncate italic">{summary}</span>
        <span className="text-[9px] text-muted-foreground/40 shrink-0">{stepCount} steps</span>
        {open ? <ChevronDown size={10} className="shrink-0 text-muted-foreground/50" /> : <ChevronRight size={10} className="shrink-0 text-muted-foreground/50" />}
      </button>
      {open && (
        <div className="border-t border-white/5 p-2 space-y-1">
          {run.logs.map((log, i) => (
            <LogEntry key={log.id || startIndex + i} log={log} isLive={isLive} index={startIndex + i} />
          ))}
        </div>
      )}
    </div>
  );
}

interface AgentQuestion {
  id: number;
  renderKey: string;
  agent: string;
  question: string;
  context: string | null;
  status: string;
  answer: string | null;
  createdAt: string;
  nudgedAt?: string | null;
  isApproval?: boolean;
  requiresApproval?: boolean;
  isNudge?: boolean;
}

function QuestionCard({ q, onAnswer, onDismiss }: { q: AgentQuestion; onAnswer: (id: number, answer: string) => void; onDismiss?: (id: number) => void }) {
  const [answer, setAnswer] = useState("");
  const [sending, setSending] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [showRevise, setShowRevise] = useState(false);

  const isApproval = q.requiresApproval ?? q.isApproval ?? false;
  const isNudge = q.isNudge ?? !!(q.nudgedAt) ?? false;

  const handleSubmit = async () => {
    if (!answer.trim() || sending) return;
    setSending(true);
    await onAnswer(q.id, answer.trim());
    setSending(false);
  };

  const handleApprove = async () => {
    if (sending) return;
    setSending(true);
    await onAnswer(q.id, "Yes, proceed");
    setSending(false);
  };

  const handleReject = () => {
    setShowRevise(true);
  };

  const handleDismiss = async () => {
    if (dismissing || !onDismiss) return;
    setDismissing(true);
    await onDismiss(q.id);
    setDismissing(false);
  };

  if (q.status === "answered") {
    return (
      <div className="border border-green-500/30 rounded-lg p-3 bg-green-950/20 my-1.5" data-testid={`question-answered-${q.id}`}>
        <div className="flex items-center gap-1.5 text-green-400 text-[10px] font-mono mb-1">
          <CheckCircle2 className="w-3 h-3" />
          Answered — {q.agent} resuming
        </div>
        <p className="text-foreground/60 text-xs">{q.question}</p>
        <p className="text-green-300/80 text-xs mt-1">→ {q.answer}</p>
      </div>
    );
  }

  return (
    <div
      className={`border rounded-lg p-3 my-1.5 ${isNudge ? "border-orange-500/50 bg-orange-950/20" : "border-amber-500/50 bg-amber-950/20"}`}
      data-testid={`question-pending-${q.id}`}
    >
      <div className="flex items-center justify-between gap-1.5 mb-1.5">
        <div className="flex items-center gap-2">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 ${
            q.agent === "Draper" ? "bg-blue-500/20 text-blue-400" :
            q.agent === "Peggy" ? "bg-pink-500/20 text-pink-400" :
            q.agent === "Pete" ? "bg-amber-500/20 text-amber-400" :
            q.agent === "Rogers" ? "bg-green-500/20 text-green-400" :
            "bg-muted/40 text-muted-foreground"
          }`}>
            {q.agent[0]}
          </div>
          <div className={`flex items-center gap-1.5 text-[10px] font-mono ${isNudge ? "text-orange-400" : "text-amber-400"}`}>
            <MessageSquare className="w-3 h-3" />
            <span className="font-bold">{q.agent}</span>
            <span className="text-muted-foreground/60">
              {isNudge ? "is still waiting for your reply" : isApproval ? "needs approval" : "is asking"}
            </span>
            {isNudge && (
              <span className="text-[8px] font-bold bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded px-1 py-0.5 ml-0.5">
                REMINDER
              </span>
            )}
          </div>
        </div>
        {onDismiss && (
          <button
            data-testid={`button-dismiss-question-${q.id}`}
            onClick={handleDismiss}
            disabled={dismissing}
            className="text-[9px] text-muted-foreground/50 hover:text-red-400 transition-colors px-1"
            title="Dismiss this question"
          >
            {dismissing ? "…" : "✕ Dismiss"}
          </button>
        )}
      </div>
      <p className="text-foreground text-xs mb-2 font-medium">{q.question}</p>
      {q.context && (
        <p className="text-amber-300/50 text-[10px] mb-2 italic border-l-2 border-amber-500/30 pl-2">{q.context}</p>
      )}

      {isApproval && !showRevise ? (
        <div className="flex gap-2 mt-2">
          <Button
            data-testid={`button-approve-question-${q.id}`}
            onClick={handleApprove}
            disabled={sending}
            size="sm"
            className="flex-1 h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5"
          >
            {sending ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />}
            Approve
          </Button>
          <Button
            data-testid={`button-reject-question-${q.id}`}
            onClick={handleReject}
            disabled={sending}
            size="sm"
            variant="outline"
            className="flex-1 h-8 border-red-500/40 text-red-400 hover:bg-red-950/30 hover:text-red-300 text-xs gap-1.5"
          >
            Reject / Revise
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            data-testid={`input-agent-question-${q.id}`}
            placeholder={isApproval && showRevise ? "Give direction before agent continues..." : "Type your answer..."}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            className="flex-1 font-mono text-xs h-8"
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            autoFocus
          />
          <Button
            data-testid={`button-answer-question-${q.id}`}
            onClick={handleSubmit}
            disabled={!answer.trim() || sending}
            size="sm"
            className="h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1"
          >
            {sending ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
            {sending ? "" : "Reply"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AgentThoughtStream() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { streamLogs, streamQuestions, isConnected, clearStream } = useAgentStream();
  const { data: historicalLogs } = useAgentLogs();
  const orchestrate = useOrchestrate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [answeredLocally, setAnsweredLocally] = useState<Record<number, string>>({});
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!activeWorkspaceId) return;
    fetch(`/api/workspaces/${activeWorkspaceId}/orchestrate/reset-stuck`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
  }, [activeWorkspaceId]);

  const abortMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/orchestrate/abort`, { sessionId });
      return res.json();
    },
    onSuccess: () => {
      setActiveSessionId(null);
      toast({ title: "Agents stopped", description: "The agent session has been aborted." });
    },
  });

  const { data: recentActivity } = useQuery<AgentLog[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "logs", "recent"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/logs?limit=8`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    refetchInterval: 15000,
  });

  const { data: workspaces } = useQuery<Workspace[]>({
    queryKey: ["/api/workspaces"],
  });

  const activeWorkspace = workspaces?.find((w) => w.id === activeWorkspaceId);

  const { data: contentStats } = useQuery<{ totalContent: number }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "stats"],
    queryFn: async () => {
      if (!activeWorkspaceId) return { totalContent: 0 };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/stats`, { credentials: "include" });
      if (!res.ok) return { totalContent: 0 };
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 60000,
  });

  const { data: pendingQuestions } = useQuery<AgentQuestion[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "agent-questions"],
    enabled: !!activeWorkspaceId,
    refetchInterval: 5000,
  });

  const answerMutation = useMutation({
    mutationFn: async ({ id, answer }: { id: number; answer: string }) => {
      const res = await apiRequest("POST", `/api/agent-questions/${id}/answer`, { answer });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || `Server error ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (_data, vars) => {
      setAnsweredLocally((prev) => ({ ...prev, [vars.id]: vars.answer }));
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "agent-questions"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to send reply", description: err.message, variant: "destructive" });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("PATCH", `/api/agent-questions/${id}/dismiss`, {});
      if (!res.ok) throw new Error("Failed to dismiss");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "agent-questions"] });
    },
    onError: () => {
      toast({ title: "Could not dismiss question", variant: "destructive" });
    },
  });

  const handleAnswer = useCallback(async (id: number, answer: string) => {
    await answerMutation.mutateAsync({ id, answer });
  }, [answerMutation]);

  const handleDismiss = useCallback(async (id: number) => {
    await dismissMutation.mutateAsync(id);
  }, [dismissMutation]);

  const isLive = streamLogs.length > 0;

  const latestStepLog = [...streamLogs].reverse().find(l => {
    const m = (l.metadata || {}) as Record<string, any>;
    return (m.iteration ?? m.step) !== undefined;
  });
  const liveStep = latestStepLog ? ((latestStepLog.metadata as any)?.iteration ?? (latestStepLog.metadata as any)?.step) : undefined;
  const liveTotal = latestStepLog ? ((latestStepLog.metadata as any)?.maxIterations ?? (latestStepLog.metadata as any)?.total) : undefined;

  const displayLogs = isLive ? streamLogs : (historicalLogs || []).slice(0, 50);

  // Merge DB-polled questions with real-time stream questions.
  // Strategy:
  //  1. Stream questions that haven't yet arrived in the DB poll are shown immediately (streamOnlyQuestions).
  //  2. Nudge entries (isNudge=true from stream) are always distinct cards — they represent a 24h reminder.
  //  3. DB-polled questions are the source of truth for durable state (nudgedAt, requiresApproval).
  const polledIds = new Set((pendingQuestions || []).map((q) => q.id));

  // Nudge stream entries are only shown if DB-polled data hasn't yet reflected nudgedAt.
  // Once the polled question shows nudgedAt set, the DB card covers the nudge display.
  const polledNudgedIds = new Set(
    (pendingQuestions || []).filter((q) => !!q.nudgedAt).map((q) => q.id)
  );

  const streamOnlyQuestions: AgentQuestion[] = streamQuestions
    .filter((sq) => {
      if (!sq.isNudge) return !polledIds.has(sq.id);
      // Nudge: show as separate card only if DB hasn't yet reflected nudgedAt
      return !polledNudgedIds.has(sq.id);
    })
    .map((sq) => ({
      id: sq.id,
      renderKey: sq.streamKey,
      agent: sq.agent,
      question: sq.question,
      context: sq.context ?? null,
      status: answeredLocally[sq.id] ? "answered" : "pending",
      answer: answeredLocally[sq.id] ?? null,
      createdAt: sq.createdAt,
      requiresApproval: sq.requiresApproval,
      isNudge: sq.isNudge,
    }));

  const polledMapped: AgentQuestion[] = (pendingQuestions || []).map((q) => ({
    ...q,
    renderKey: String(q.id),
    status: answeredLocally[q.id] ? "answered" : q.status,
    answer: answeredLocally[q.id] || q.answer,
    createdAt: q.createdAt ? String(q.createdAt) : new Date().toISOString(),
    nudgedAt: q.nudgedAt ? String(q.nudgedAt) : null,
  }));

  const displayQuestions = [...polledMapped, ...streamOnlyQuestions].sort((a, b) => {
    const aNudged = a.nudgedAt ? new Date(a.nudgedAt).getTime() : 0;
    const bNudged = b.nudgedAt ? new Date(b.nudgedAt).getTime() : 0;
    if (aNudged !== bNudged) return bNudged - aNudged;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [displayLogs, streamQuestions]);

  // Auto-expand panel when there are pending questions needing user input
  const pendingCount = displayQuestions.filter(q => q.status === "pending").length;
  useEffect(() => {
    if (pendingCount > 0) {
      setExpanded(true);
    }
  }, [pendingCount]);

  const handleLaunch = useCallback(async () => {
    if (!activeWorkspace) return;
    clearStream();
    setAnsweredLocally({});
    queryClient.setQueryData(["/api/workspaces", activeWorkspaceId, "agent-questions"], []);
    try {
      const result = await orchestrate.mutateAsync({
        url: activeWorkspace.url || "",
        businessName: activeWorkspace.name,
      });
      if (result?.sessionId) {
        setActiveSessionId(result.sessionId);
        // Clear after 10 minutes (max session time)
        setTimeout(() => setActiveSessionId(null), 10 * 60 * 1000);
      }
    } catch {}
  }, [activeWorkspace, activeWorkspaceId, orchestrate, clearStream, queryClient]);

  const agents = ["Draper", "Peggy", "Pete", "Rogers"];
  const agentLastActive: Record<string, string | null> = {};
  agents.forEach((agent) => {
    const logs = (historicalLogs || []).filter((l) => l.agent === agent);
    agentLastActive[agent] = logs.length > 0 ? logs[0].createdAt as unknown as string : null;
  });

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-border bg-card"
      data-testid="agent-thought-stream"
    >
      <Starfield />

      <div className="relative z-10">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Sparkles size={18} className="text-blue-400" />
              {isLive && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              )}
            </div>
            <div>
              <h3 className="text-sm font-display font-bold text-foreground">
                Agent Command Center
              </h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                {isLive ? (
                  <span className="flex items-center gap-1 text-[10px] text-green-400"><Radio size={8} className="animate-pulse" /> Live</span>
                ) : (
                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground/60"><History size={8} /> Recent history</span>
                )}
                {isLive && liveStep != null && liveTotal != null && (
                  <span className="flex items-center gap-1" data-testid="step-counter">
                    <span className="text-[9px] font-mono text-indigo-300/80">Step {liveStep}/{liveTotal}</span>
                    <div className="h-1 rounded-full bg-white/10 w-16">
                      <div className="h-1 rounded-full bg-indigo-400/60 transition-all duration-500" style={{ width: `${Math.min(Math.round((liveStep / liveTotal) * 100), 100)}%` }} />
                    </div>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              {agents.map((agent) => (
                <div key={agent} className="flex flex-col items-center gap-0.5" title={`${agent} · ${AGENT_ROLES[agent]}${agentLastActive[agent] ? ` · ${timeAgo(agentLastActive[agent]!)}` : ""}`}>
                  <div className={`w-2 h-2 rounded-full ${AGENT_DOT_COLORS[agent]} ${agentLastActive[agent] ? "opacity-100" : "opacity-30"}`} />
                  <span className={`text-[8px] font-bold ${AGENT_COLORS[agent]} opacity-70`}>{agent[0]}</span>
                </div>
              ))}
            </div>

            {activeWorkspace && (
              <div className="flex items-center gap-2">
                {activeSessionId && (
                  <Button
                    size="sm"
                    onClick={() => abortMutation.mutate(activeSessionId)}
                    disabled={abortMutation.isPending}
                    data-testid="button-stop-agents"
                    className="bg-red-600/80 hover:bg-red-600 text-white text-xs gap-1.5"
                  >
                    {abortMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Square size={12} />}
                    Stop
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleLaunch}
                  disabled={orchestrate.isPending || !!activeSessionId}
                  data-testid="button-launch-agents"
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5"
                >
                  {orchestrate.isPending ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Play size={12} />
                  )}
                  {orchestrate.isPending ? "Starting..." : "Run Agents"}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div
          ref={scrollRef}
          className={`overflow-y-auto font-mono text-xs transition-all duration-300 ${
            expanded ? "max-h-[500px]" : "max-h-[280px]"
          }`}
          data-testid="thought-stream-log"
        >
          {displayLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <BrainCircuit size={32} className="text-muted-foreground/30 mb-3" />
              {activeWorkspace && !activeWorkspace.url && !activeWorkspace.industry && !activeWorkspace.targetAudience ? (
                <>
                  <p className="text-amber-400/80 text-center text-xs mb-1 font-semibold">Workspace needs setup</p>
                  <p className="text-muted-foreground/60 text-center text-[10px] mb-3">
                    Add your website URL, industry, and target audience in Settings so agents have context to work with.
                  </p>
                  <Button
                    size="sm"
                    onClick={handleLaunch}
                    disabled={orchestrate.isPending}
                    className="bg-blue-600/80 hover:bg-blue-600 text-white text-xs gap-1.5"
                  >
                    <Play size={11} />
                    Launch Anyway
                  </Button>
                </>
              ) : (
                <>
                  {(contentStats?.totalContent ?? 0) > 0 ? (
                    <>
                      <p className="text-foreground/80 text-center text-xs font-medium mb-1" data-testid="text-command-center-context">
                        Draper set up your workspace with {contentStats?.totalContent} content items.
                      </p>
                      <p className="text-muted-foreground/50 text-center text-[10px] mb-3">
                        Run agents to keep the content engine running autonomously.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-muted-foreground/70 text-center text-xs mb-1">
                        No agent activity yet.
                      </p>
                      <p className="text-muted-foreground/50 text-center text-[10px] mb-3">
                        Draper + Peggy will plan and create today's content automatically.
                      </p>
                    </>
                  )}
                  {activeWorkspace && (
                    <div className="flex flex-col items-center gap-1.5">
                      <Button
                        size="sm"
                        onClick={handleLaunch}
                        disabled={orchestrate.isPending}
                        className="bg-blue-600/80 hover:bg-blue-600 text-white text-xs gap-1.5"
                        title="Draper + Peggy will plan and create today's content automatically"
                        data-testid="button-run-agents"
                      >
                        <Play size={11} />
                        Run Agents
                      </Button>
                      <span className="text-[9px] text-muted-foreground/40">Draper + Peggy will plan and create today's content automatically.</span>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="p-3 space-y-1">
              {!isLive && displayLogs.length > 0 && (
                <div className="text-[9px] text-muted-foreground/40 uppercase tracking-wider px-2 pb-1 flex items-center gap-1">
                  <History size={8} /> Showing last {displayLogs.length} actions — run agents to see live stream
                </div>
              )}
              {displayQuestions.filter(q => q.status === "pending").length > 0 && (
                <div className="px-1">
                  {displayQuestions.filter(q => q.status === "pending").map((q) => (
                    <QuestionCard key={q.renderKey} q={q} onAnswer={handleAnswer} onDismiss={handleDismiss} />
                  ))}
                </div>
              )}
              {(() => {
                const runs = groupLogsIntoRuns(displayLogs);
                let offset = 0;
                return runs.map((run) => {
                  const startIdx = offset;
                  offset += run.logs.length;
                  return <AgentRunGroup key={run.id} run={run} isLive={isLive} startIndex={startIdx} />;
                });
              })()}
              {displayQuestions.filter(q => q.status === "answered").length > 0 && (
                <div className="px-1">
                  {displayQuestions.filter(q => q.status === "answered").slice(0, 3).map((q) => (
                    <QuestionCard key={q.renderKey} q={q} onAnswer={handleAnswer} />
                  ))}
                </div>
              )}
              {orchestrate.isPending && (
                <div className="flex items-center gap-2 pl-4 py-2 text-muted-foreground/70">
                  <Loader2 size={10} className="animate-spin" />
                  <span>Agents are thinking...</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-t border-border">
          <span className="text-[10px] text-muted-foreground/50">
            {displayLogs.length} events
          </span>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] text-muted-foreground/70 hover:text-muted-foreground transition-colors"
            data-testid="button-toggle-stream"
          >
            {expanded ? "Collapse" : "Expand"}
          </button>
        </div>

        {!isLive && recentActivity && recentActivity.length > 0 && (
          <div className="border-t border-border px-4 py-3" data-testid="recent-agent-activity">
            <div className="flex items-center gap-1.5 mb-2">
              <History size={10} className="text-muted-foreground/50" />
              <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Recent Agent Activity</span>
            </div>
            <div className="space-y-1.5">
              {recentActivity.slice(0, 6).map((log, i) => (
                <div key={log.id || i} className="flex items-start gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${
                    log.type === "error" ? "bg-red-500" :
                    log.type === "warning" ? "bg-amber-400" :
                    log.type === "milestone" ? "bg-emerald-500" :
                    log.type === "action" ? "bg-amber-500" :
                    AGENT_DOT_COLORS[log.agent] || "bg-muted-foreground/30"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <span className={`text-[10px] font-bold mr-1 ${AGENT_COLORS[log.agent] || "text-muted-foreground"}`}>{log.agent}</span>
                    <span className="text-[10px] text-muted-foreground/60 truncate">{log.action}</span>
                  </div>
                  {log.createdAt && (
                    <span className="text-[9px] text-muted-foreground/30 shrink-0">{timeAgo(log.createdAt as unknown as string)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
